import { AtkSummary } from "portal/views/autotractor/AtkSummary";
import { buildPermission } from "portal/utils/auth";
import { Config } from "portal/components/config/Config";
import {
  getRobotPath,
  getSwitchRigPath,
  Path,
  RobotSubpath,
  Slug,
} from "portal/utils/routing";
import { GlobalHotKeysProps, KeyMap } from "react-hotkeys";
import { GROUP_ROBOT } from "portal/utils/hotkeys";
import { NavPage } from "portal/components/ResponsiveSubnav";
import { NewRobotField } from "portal/views/robots/NewRobotField";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { RobotAdmin } from "portal/views/robots/RobotAdmin";
import { RobotFieldList } from "portal/views/robots/RobotFieldList";
import { RobotSummaryResponse } from "protos/portal/robots";
import { RobotSupport } from "portal/views/robots/RobotSupport";
import { RobotView } from "portal/views/robots/AnyRobotPage";
import { RouteObject, useNavigate, useParams } from "react-router-dom";
import { titleCase } from "portal/utils/strings";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useTranslation } from "react-i18next";
import React from "react";

export interface RobotPageNav {
  keymap: KeyMap;
  globalHotKeysHandlers: GlobalHotKeysProps["handlers"];
  routes: RouteObject[];
  subnavPages: NavPage[];
}

export function useAtkRobotPageNav(
  offerViewSwitchButton: RobotView | undefined,
  summary: RobotSummaryResponse | undefined
): RobotPageNav {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ### ROBOT
  const { serial } = useParams();

  // ### ROUTING
  const keyMap: KeyMap = {
    GO_SUMMARY: {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.summary.navTitle"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r s",
    },
  };
  const routes: RouteObject[] = [
    {
      path: Path.BASE,
      element: <AtkSummary offerViewSwitchButton={offerViewSwitchButton} />,
    },
  ];

  // chat
  const isChatAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.chat,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.chat,
        PermissionDomain.all
      ),
    ],
  });
  if (isChatAuthorized) {
    routes.push({ path: RobotSubpath.SUPPORT, element: <RobotSupport /> });
    keyMap.GO_SUPPORT = {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.support.navTitle"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r t",
    };
  }

  // config
  const isConfigAuthorized = useAuthorizationRequired(
    // NOTE: In principle, this should also allow `read:configs:customer`.
    // But currently (1) we don't actually want customers to see configs, yet
    // (2) we're granting all customers read-write permissions to their own
    // configs to unbreak things like crops that use configs as a backing store
    // and don't have a smart deputy yet. So we leave the API open but just
    // hide the UI.
    buildPermission(
      PermissionAction.read,
      PermissionResource.configs,
      PermissionDomain.all
    )
  );
  if (isConfigAuthorized) {
    routes.push({
      path: `${RobotSubpath.CONFIG}/*`,
      element: (
        <Config
          serial={serial}
          customerSerial={summary?.robot?.customerSerial}
          basePath={getRobotPath(serial, RobotSubpath.CONFIG)}
        />
      ),
    });
    keyMap.GO_CONFIG = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.configs.config_one")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r c",
    };
  }

  // admin
  const isAdminAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.admin_cloud,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.admin_cloud,
        PermissionDomain.all
      ),
    ],
  });
  if (isAdminAuthorized) {
    routes.push({ path: RobotSubpath.ADMIN, element: <RobotAdmin /> });
    keyMap.GO_ADMIN = {
      name: t("utils.actions.goToLong", {
        subject: t("views.admin.title"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r a",
    };
  }

  // farms
  const isFarmsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.all
      ),
    ],
  });
  if (isFarmsAuthorized) {
    routes.push(
      {
        path: `${RobotSubpath.FIELDS}`,
        element: <RobotFieldList />,
      },
      {
        path: `${RobotSubpath.FIELDS}/${Slug.NEW}`,
        element: <NewRobotField serial={serial} />,
      }
    );
    keyMap.GO_FIELDS = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.fieldDefinitions.fieldDefinition_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r f",
    };
  }

  return {
    keymap: keyMap,
    globalHotKeysHandlers: {
      GO_SUMMARY: () => navigate(getRobotPath(serial)),
      GO_CONFIG: () => navigate(getRobotPath(serial, RobotSubpath.CONFIG)),
      GO_SUPPORT: () => navigate(getRobotPath(serial, RobotSubpath.SUPPORT)),
      GO_ADMIN: () => navigate(getRobotPath(serial, RobotSubpath.ADMIN)),
    },
    routes,
    subnavPages: [
      {
        label: t("views.fleet.robots.summary.navTitle"),
        to: getRobotPath(serial),
      },
      {
        label: "equipment", //FIXME
        to: getSwitchRigPath(serial!),
        hidden: !serial,
      },
      {
        label: titleCase(t("models.configs.config_one")),
        to: `${getRobotPath(serial, RobotSubpath.CONFIG)}/*`,
        hidden: !isConfigAuthorized,
      },
      {
        label: t("views.fleet.robots.support.navTitle"),
        to: getRobotPath(serial, RobotSubpath.SUPPORT),
        hidden: !isChatAuthorized,
      },
      {
        label: t("views.admin.title"),
        to: getRobotPath(serial, RobotSubpath.ADMIN),
        hidden: !isAdminAuthorized,
      },
    ],
  };
}
